import { InternalUserResponseDto } from "@/lib/api/interfaces/internal.interface";
import { useUsersInternalGetUser, useUsersInternalUpdateUser } from "@/lib/api/requests/internal.requests";
import { Button } from "@/ui-components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/ui-components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "@/lib/toast";
import z from "zod";

const UserDetailsDialog = ({
  open,
  onClose,
  userId,
}: {
  open: boolean;
  onClose: (refetchData: boolean) => void;
  userId: string;
}) => {
  const { data: user } = useUsersInternalGetUser(userId);

  const onFormSubmit = () => {
    onClose(true);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nutzerdetails</DialogTitle>
        </DialogHeader>
        <UserDetailsWrapper user={user} onFormSubmit={onFormSubmit} />
      </DialogContent>
    </Dialog>
  );
};

const UserDetailsWrapper = ({ user, onFormSubmit }: { user: InternalUserResponseDto; onFormSubmit: () => void }) => {
  const [isEditing, setIsEditing] = useState(false);

  return (
    <div>
      <div className="flex justify-end">
        <Button variant="outline" onClick={() => setIsEditing(!isEditing)} className="flex items-center gap-2">
          {isEditing ? "Abbrechen" : "Bearbeiten"}
          <Pencil className="w-4 h-4" />
        </Button>
      </div>
      {isEditing ? <UserEditForm onFormSubmit={onFormSubmit} user={user} /> : <UserDetails user={user} />}
    </div>
  );
};

const UserDetails = ({ user }: { user: InternalUserResponseDto }) => {
  return (
    <div className="space-y-6 p-4">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">Vorname</h3>
            <p className="text-base font-medium">{user.firstName}</p>
          </div>
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">Nachname</h3>
            <p className="text-base font-medium">{user.lastName}</p>
          </div>
        </div>
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground">E-Mail</h3>
          <p className="text-base font-medium">{user.mail}</p>
        </div>
      </div>
    </div>
  );
};

const UserEditForm = ({ onFormSubmit, user }: { onFormSubmit: () => void; user: InternalUserResponseDto }) => {
  const { mutate: updateUser } = useUsersInternalUpdateUser();

  const updateUserSchema = z.object({
    firstName: z.string().min(1, {
      message: "Vorname ist erforderlich",
    }),
    lastName: z.string().min(1, {
      message: "Nachname ist erforderlich",
    }),
    mail: z.string().email("Bitte geben Sie Ihre Email-Adresse ein"),
  });

  type UpdateUserSchema = z.infer<typeof updateUserSchema>;

  const form = useForm<UpdateUserSchema>({
    resolver: zodResolver(updateUserSchema),
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      mail: user.mail,
    },
  });

  const onSubmit = async (data: UpdateUserSchema) => {
    updateUser(
      {
        id: user._id,
        data: {
          firstName: data.firstName,
          lastName: data.lastName,
          mail: data.mail,
        },
      },
      {
        onSuccess: () => {
          toast.success("Nutzer wurde erfolgreich aktualisiert");
          onFormSubmit();
        },
        onError: (error) => {
          toast.error("Nutzer konnte nicht aktualisiert werden", { error });
        },
      }
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="flex gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel isOptional={false}>Vorname</FormLabel>
                <FormControl>
                  <Input placeholder="Max" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="w-1/2">
                <FormLabel isOptional={false}>Nachname</FormLabel>
                <FormControl>
                  <Input placeholder="Mustermann" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="w-full">
          <FormField
            control={form.control}
            name="mail"
            render={({ field }) => (
              <FormItem>
                <FormLabel isOptional={false}>Email</FormLabel>
                <FormControl>
                  <Input placeholder="max.mustermann@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="w-full flex justify-end mt-4">
          <Button type="submit">Speichern</Button>
        </div>
      </form>
    </Form>
  );
};

export default UserDetailsDialog;
